/**
 * Road network. Roads are player-placed on buildable/passable tiles and make
 * traversal cheaper, so pathfinding naturally prefers them (workers "use the
 * roads"). Stored as a flat Uint8 layer parallel to the map grid — O(1) queries,
 * trivial to serialise for saves.
 */
import type { GameMap } from '../map/gamemap.js';

/** Multiplier applied to a tile's base movement cost when a road is present.
 * < 1 makes roads attractive to the A* cost function. */
export const ROAD_COST_FACTOR = 0.4;

export class RoadNetwork {
  private readonly roads: Uint8Array;

  constructor(private readonly map: GameMap) {
    this.roads = new Uint8Array(map.tileCount);
  }

  has(x: number, y: number): boolean {
    if (!this.map.inBounds(x, y)) return false;
    return this.roads[y * this.map.width + x] === 1;
  }

  /** Place a road. Returns false if the tile cannot host one (impassable). */
  place(x: number, y: number): boolean {
    if (!this.map.inBounds(x, y)) return false;
    if (!Number.isFinite(this.map.moveCostAt(x, y))) return false;
    this.roads[y * this.map.width + x] = 1;
    return true;
  }

  remove(x: number, y: number): void {
    if (!this.map.inBounds(x, y)) return;
    this.roads[y * this.map.width + x] = 0;
  }

  /** Raw layer (read-only) for rendering / serialisation. */
  get layer(): Readonly<Uint8Array> {
    return this.roads;
  }

  count(): number {
    let n = 0;
    for (let i = 0; i < this.roads.length; i++) if (this.roads[i] === 1) n++;
    return n;
  }
}
